// @soft-depth
import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { Speech } from 'lucide-react';
import { COVER_ANIMALS, deriveCoverDisc } from '../../../views/CoverQuiz/coverQuizData';
import { discLabel, isDiscLetter } from '../../../constants/disc';
import { useCoverQuiz } from '../../../views/CoverQuiz/coverQuizStorage';
import DiscTips from './DiscTips';
import { IdeaCard, IdeaOpener } from './IdeaKit';

/**
 * Smart IDEA 溝通方式 — DISC 對話建議, the business twin of 招募 IDEA's card of
 * the same name: the type in the head, the tips (DiscTips), and the line to
 * open with last, once the adviser has read who they are talking to. Reads the
 * letter on file; when there is none, the letter the client's 保障小測驗
 * implies, labelled as such until the adviser applies it.
 */

type IntlLike = {
  formatMessage: (descriptor: { id: string }, values?: Record<string, unknown>) => string;
  locale?: string;
};

export interface PlaybookCardProps {
  clientId?: string | null;
  /** For 今日見 {name}，開口就問： */
  clientName?: string;
  personality?: string | null;
  intl: IntlLike;
  cardRef?: React.Ref<HTMLElement>;
}

const PlaybookCard = (props: PlaybookCardProps) => {
  const { clientId, clientName, personality, intl, cardRef } = props;
  const t = (id: string, values?: Record<string, unknown>) => intl.formatMessage({ id }, values);
  const quiz = useCoverQuiz(clientId);
  const onFile = isDiscLetter(personality) ? personality : null;
  const fromQuiz = !onFile && quiz ? deriveCoverDisc(quiz.answers) : null;
  const letter = onFile || fromQuiz;

  return (
    <IdeaCard
      id="how"
      testId="playbook-card"
      cardRef={cardRef}
      icon={<Speech size={18} aria-hidden="true" />}
      title={t('smart-idea-playbook-title')}
      action={
        letter && (
          // The type in its own hue, as on the 保障小測驗 and the persona card.
          <Chip
            size="small"
            label={discLabel(letter, intl.locale)}
            sx={{
              ml: 'auto',
              flexShrink: 0,
              bgcolor: COVER_ANIMALS[letter].bg,
              borderColor: COVER_ANIMALS[letter].bg,
              color: COVER_ANIMALS[letter].color,
              fontWeight: 700
            }}
          />
        )
      }
    >
      {letter ? (
        <>
          {/* Where the letter comes from: on file, or only implied by the quiz. */}
          <Typography
            component="p"
            variant="subtitle2"
            data-testid="playbook-source"
            data-source={fromQuiz ? 'quiz' : 'file'}
            sx={{
              m: 0,
              mb: 1.25,
              px: 1.25,
              py: 0.75,
              borderRadius: 1,
              bgcolor: fromQuiz ? 'primary.soft' : 'grey.100',
              color: fromQuiz ? 'primary.dark' : 'text.primary'
            }}
          >
            {t(fromQuiz ? 'smart-idea-playbook-source-quiz' : 'smart-idea-playbook-source-file')}
          </Typography>
          <DiscTips mode="business" letter={letter} intl={intl} testId="disc-playbook" />
          <Box data-testid="playbook-opener" sx={{ mt: 2, pt: 1.75, borderTop: '1px dashed', borderColor: 'divider' }}>
            <IdeaOpener
              title={t('clientv2-recruit-approach')}
              lead={t('clientv2-recruit-talkcard-line', { name: clientName || '' })}
              lines={[t(`disc-playbook-business-${letter}-ask`)]}
              fallback={t(`disc-playbook-business-${letter}-ask`)}
              refreshLabel={t('clientv2-recruit-talkcard-refresh')}
            />
          </Box>
        </>
      ) : (
        <Typography component="p" variant="body2" data-testid="playbook-empty" sx={{ m: 0, color: 'text.secondary' }}>
          {t('smart-idea-playbook-empty')}
        </Typography>
      )}
    </IdeaCard>
  );
};

export default PlaybookCard;
